import Form                from '@widesk-ui/components/form/Form';
import Button              from '@widesk-ui/components/Button';
import { SwapOutlined }    from '@ant-design/icons';
import SelectProject       from '@selects/map/SelectProject';
import ProjectModel        from '@models/map/ProjectModel';
import PlaceTypeModel      from '@models/map/PlaceTypeModel';
import useApiCollection    from '@widesk-core/hooks/useApiCollection';
import Divider             from '@widesk-ui/components/Divider';
import Checkbox            from '@widesk-ui/components/form/Checkbox';
import Row                 from '@widesk-ui/components/Row';
import { getIdFromUrn }    from '@widesk-core/tools/identifier';
import { CloseSquareOutlined } from '@ant-design/icons';
import { CheckSquareOutlined } from '@ant-design/icons';
import React from 'react';
import Spin from '@widesk-ui/components/Spin';
import View from '@widesk-ui/components/View';
import ApiCollection from '@widesk-core/models/ApiCollection';

export type ProjectPlaceTypeCopyForm = {
	onSuccess?: () => void;
	projectUrn?: Urn;
}

export default function ProjectPlaceTypeCopyForm({ onSuccess, projectUrn }: ProjectPlaceTypeCopyForm) {
	const collPlaceType = useApiCollection(PlaceTypeModel);
	const [selectedPlaceTypes, setSelectedPlaceTypes] = React.useState<id[]>();

	const allSelected = collPlaceType.length === selectedPlaceTypes?.length;

	return (
		<Form
			onSuccess={onSuccess}
			onDidMount={async formTools => {
				formTools.setFieldValue('to', projectUrn);
				collPlaceType.setSort('label', 'asc');
			}}
			fields={formTools => ({
				from: {
					label: `Copier depuis le ${ProjectModel.localize('default', 'l')}`,
					element: (
						<SelectProject
							disabledOption={o => o.urn === projectUrn}
							disabled={formTools.getFieldValue('from') === projectUrn}
							onChange={async value => {
								await collPlaceType.listBy([getIdFromUrn(value as Urn)], 'project');
								formTools.validateFields();
							}}
						/>
					),
					span: 9,
					required: true,
				},
				reverse: {
					colStyle: {
						alignItems: 'flex-end',
						display: 'flex',
						justifyContent: 'center',
					},
					element: (
						<Button
							icon={<SwapOutlined />}
							onClick={async () => {
								const values = formTools.getValues();
								formTools.setFieldValue('from', values.to);
								formTools.setFieldValue('to', values.from);

								await collPlaceType.listBy([getIdFromUrn(values.to as Urn)], 'project');
								formTools.validateFields();
							}}
						>
							Inverser
						</Button>
					),
					noFormItem: true,
					span: 5,
				},
				to: {
					label: `Copier vers le ${ProjectModel.localize('default', 'l')}`,
					element: (
						<SelectProject
							disabled={formTools.getFieldValue('to') === projectUrn}
							disabledOption={o => o.urn === projectUrn}
						/>
					),
					span: 10,
					required: true,
				},
				divider: {
					hidden: !collPlaceType.isLoaded,
					noFormItem: true,
					element: <Divider size="small">Sélection des {PlaceTypeModel.localize('plural', 'l')}</Divider>,
					span: 24,
				},
				placeTypes: {
					hidden: !collPlaceType.isLoaded,
					label: PlaceTypeModel.localize('plural'),
					element: (
						<Checkbox.Group onChange={(v: any) => setSelectedPlaceTypes(v)}>
							<Spin  widthF spinning={collPlaceType.isLoading}>
								{collPlaceType.length ? (
									<Row>
										{collPlaceType.map(pt => (
											<Row.Col span={12}>
												<Checkbox value={pt.id}>{pt.label}</Checkbox>
											</Row.Col>
										))}
									</Row>
								) : <View italic>Aucun type dans le projet source</View>}
							</Spin>
						</Checkbox.Group>
					),
					span: 24,
					extra: (
						<View left mt="xs">
							<Button
								disabled={!collPlaceType.length}
								danger={allSelected}
								icon={allSelected ? <CloseSquareOutlined /> : <CheckSquareOutlined />}
								onClick={() => {
									const newValue = allSelected ? [] : collPlaceType.ids;
									formTools.setFieldValue('placeTypes', newValue);
									setSelectedPlaceTypes(newValue);
								}}
							>
								Tout {allSelected ? 'dé' : ''}sélectionner
							</Button>
						</View>
					),
					required: true,
				},
			})}
			updateOnChange
			successMessage={`Les ${PlaceTypeModel.localize('plural', 'l')} ont bien été copiés.`}
			onSubmit={async values => {
				const selected = collPlaceType.filterByIncluded('id', values.placeTypes as id[]);

				const coll = new ApiCollection(PlaceTypeModel, selected.map(selectedPlaceType => {
					return new PlaceTypeModel({
						label: selectedPlaceType.label,
						reference: selectedPlaceType.reference,
						project: projectUrn,
					});
				}));

				await coll.save();
				return coll.first();
			}}
		/>
	);
}